import { useState } from "react";
import type { MockMessageMedia } from "@/dummy";
import { GifVideo } from "./gif-video";
import { fitMedia } from "./media-box";

/** Width (px) of a single video whose intrinsic size is unknown (before the BE serves dims). */
const FALLBACK_VIDEO_WIDTH = 220;

/**
 * Video tile. A single video reserves a DEFINITE box before anything arrives (like `ImageTile`):
 * an unloaded `<video>` is 0px tall (Tailwind's `h-auto` also overrides the `height` attribute), so
 * the box used to appear only when the poster or the first frame landed, growing the thread under a
 * reader pinned to the newest message.
 *
 * KNOWN dims: width + aspect-ratio from the dims, capped at `MAX_MEDIA_PX`. UNKNOWN dims: a 16:9 box
 * that settles to the real ratio once the metadata reports it. A single video plays inline (native
 * controls); a grid video keeps its cell and gets an invisible lightbox click target over it.
 */
export function VideoTile({
  item,
  multi,
  onOpen,
  label,
}: {
  item: MockMessageMedia;
  multi: boolean;
  onOpen: () => void;
  label: string;
}) {
  // Aspect measured from the metadata, for the dim-less single video.
  const [measured, setMeasured] = useState<string | null>(null);

  if (multi) {
    return (
      <div className="relative w-full overflow-hidden">
        {/* biome-ignore lint/a11y/useMediaCaption: visitor / operator uploads carry no caption track */}
        <video
          src={item.url}
          poster={item.poster}
          controls
          preload="metadata"
          className="block h-full w-full object-cover"
        />
        {/* Invisible click target over a GRID video only. */}
        <button
          type="button"
          aria-label={label}
          onClick={onOpen}
          className="absolute inset-0 cursor-zoom-in"
        />
      </div>
    );
  }

  const known = item.width && item.height ? fitMedia(item.width, item.height) : null;
  return (
    <div
      style={{
        width: `${known ? known.width : FALLBACK_VIDEO_WIDTH}px`,
        aspectRatio: known ? `${known.width} / ${known.height}` : (measured ?? "16 / 9"),
        maxWidth: "100%",
        ...(known ? {} : { maxHeight: "20rem" }),
      }}
      className="relative block overflow-hidden"
    >
      {item.gif ? (
        <GifVideo item={item} />
      ) : (
        // biome-ignore lint/a11y/useMediaCaption: visitor / operator uploads carry no caption track
        <video
          src={item.url}
          poster={item.poster}
          controls
          preload="metadata"
          onLoadedMetadata={
            known
              ? undefined
              : (e) => {
                  const v = e.currentTarget;
                  if (v.videoWidth && v.videoHeight)
                    setMeasured(`${v.videoWidth} / ${v.videoHeight}`);
                }
          }
          className="absolute inset-0 block h-full w-full object-contain"
        />
      )}
    </div>
  );
}
